import { superscript } from "./superscript.js";

// How the app names a source page: by its site's name, first letter capital and without the
// ".com" ("Yahoo"), and, for a list of sources, a readable title made from the page's own address.

// Public suffixes of two labels, under which the site's name is the THIRD label from the end
// ("news.bbc.co.uk" → "bbc"). A deliberately small subset of the Public Suffix List: a full
// public-suffix trie weighs more of the main chunk than a display label is worth. What a miss
// costs: a host under an unlisted second-level suffix ("example.co.il", "example.com.ar") reads
// as "Co" / "Com" instead of "Example". Add an entry here when a real source shows the defect.
const SECOND_LEVEL_SUFFIXES = new Set(
  Object.freeze([
    "co.uk",
    "org.uk",
    "ac.uk",
    "gov.uk",
    "com.au",
    "net.au",
    "org.au",
    "gov.au",
    "co.jp",
    "ac.jp",
    "co.nz",
    "co.za",
    "com.br",
    "com.cn",
    "co.in",
    "co.kr",
    "com.mx",
    "com.sg",
    "com.hk",
    "com.tr",
  ]),
);

/**
 * "finance.yahoo.com" → "Yahoo"; "www.bbc.co.uk" → "Bbc". The name is the label before the
 * public suffix — the last label, or the last two when `SECOND_LEVEL_SUFFIXES` lists the pair.
 * A host with no such name (an IP address, "localhost", "co.uk" alone) reads as the host
 * itself, normalised: lower-cased, without a "www." prefix, a trailing dot or a ":port".
 * @param {string} host
 * @returns {string}
 */
export function siteNameOf(host) {
  const h = String(host || "")
    .trim()
    .toLowerCase()
    // A ":port" after a bracketed IPv6 literal or a colon-free host; a bare "::1" keeps its colons.
    // Stripped first: a trailing dot may sit before it ("example.com.:443").
    .replace(/^(\[[^\]]*\]|[^:]+):\d+$/, "$1")
    .replace(/\.$/, "")
    .replace(/^www\d?\./, "");
  if (h.includes(":") || /^\d{1,3}(\.\d{1,3}){3}$/.test(h)) return h;
  const labels = h.split(".");
  const suffixLabels = SECOND_LEVEL_SUFFIXES.has(labels.slice(-2).join(".")) ? 2 : 1;
  const name = labels[labels.length - 1 - suffixLabels];
  return name ? name[0].toUpperCase() + name.slice(1) : h;
}

/**
 * "https://finance.yahoo.com/economy/…" → "Yahoo"; "https://www.bbc.co.uk/…" → "Bbc".
 * Not a URL → "" (callers show the text as written).
 * @param {string} url
 * @returns {string}
 */
export function sourceNameOf(url) {
  let host;
  try {
    host = new URL(String(url)).hostname;
  } catch {
    return "";
  }
  return siteNameOf(host);
}

/**
 * A readable title for a source page from its address: its last meaningful path segment,
 * de-slugged ("goldman-sachs-pushes-fed-rate-124429933.html" → "Goldman Sachs pushes fed rate").
 * Empty when the address says nothing more than its domain.
 * @param {string} url
 * @returns {string}
 */
export function sourceTitleOf(url) {
  let path;
  try {
    path = new URL(String(url)).pathname;
  } catch {
    return "";
  }
  const segs = path
    .split("/")
    .map((s) => {
      try {
        return decodeURIComponent(s);
      } catch {
        return s;
      }
    })
    .map((s) =>
      s
        .replace(/\.(?:html?|php|aspx?|pdf)$/i, "")
        .replace(/[-_]\d{5,}$/, "")
        .replace(/[-_+]+/g, " ")
        // Slugs spell out decimals and percents ("3point3percent").
        .replace(/(\d)point(\d)/gi, "$1.$2")
        .replace(/(\d)percent\b/gi, "$1%")
        // A trailing id token mixing digits and letters ("… 2027 93CH").
        .replace(/\s+(?=[a-z]*\d)(?=\d*[a-z])[a-z0-9]{2,10}$/i, "")
        .trim(),
    )
    .filter((s) => /[a-z]{3}/i.test(s) && s.split(" ").length >= 2);
  const seg = segs[segs.length - 1] || "";
  if (!seg) return "";
  const clipped = seg.length > 90 ? `${seg.slice(0, 90).replace(/\s+\S*$/, "")}…` : seg;
  return clipped[0].toUpperCase() + clipped.slice(1);
}

/**
 * Each address's site name, numbered where the list holds several pages under one name
 * ("Reuters¹", "Reuters²") so two different pages never read as one source. The numbers
 * follow the list's order, so pass the report's sources in first-seen order.
 * @param {string[]} urls
 * @returns {Map<string, string>}
 */
export function numberedSourceLabels(urls) {
  const byName = new Map();
  for (const url of urls || []) {
    const name = sourceNameOf(url);
    if (!name) continue;
    if (!byName.has(name)) byName.set(name, []);
    const pages = byName.get(name);
    if (!pages.includes(url)) pages.push(url);
  }
  const labels = new Map();
  for (const [name, pages] of byName) {
    pages.forEach((url, i) => labels.set(url, pages.length > 1 ? name + superscript(i + 1) : name));
  }
  return labels;
}

/** A bare figure ("$42M", "38%", "2,853,299") sets in the tabular face; "4 of 7" or a date does not. */
export function isPlainFigure(v) {
  return /^[-+−]?[$€£]?\d[\d.,]*\s?[%KMBkmbx]?$/.test(String(v ?? "").trim());
}
